import { useRef, useEffect, useMemo, useState, FC } from 'react';
import './ArticlesSearch.scss';
import MainNavbar from '../../../components/MainNavbar/MainNavbar';
import { Link, useParams } from 'react-router-dom';
import FormAppHome from '../../../components/AppHome/FormAppHome/FormAppHome';
import { Col, Container, Row } from 'react-bootstrap';
import { useArticleSearch } from '../../../providers/Articles/ArticlesSearch/ArticlesSearchProvider';
import { ArticleSearchModel } from '../../../models/Articles/ArticleSearch/ArticleSearchModel';
import { BvIcon } from 'bevi-icon';
import Skeleton from 'react-loading-skeleton';

interface ArticlesSearchProps { }

type CachePayload = {
  question: string;
  articles: ArticleSearchModel[];
  total: number;
  timestamp: number;
};

const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h
const CACHE_KEY = 'articles_search:last';

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const expired = Date.now() - parsed.timestamp > CACHE_TTL_MS;
    return expired ? null : parsed;
  } catch { return null; }
}
function writeCache(payload: any) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(payload)); } catch { }
}

const ArticlesSearch: FC<ArticlesSearchProps> = () => {
  const [search, setSearch] = useState('');
  const [cachedArticles, setCachedArticles] = useState<any[] | null>(null);

  // LOCKs para evitar loop
  const fetchedOnceRef = useRef(false);          // evita re-fetch
  const hydratedFromCacheRef = useRef(false);    // marca se veio do cache

  const { question } = useParams<{ question: string }>();
  const decodedQuestion = useMemo(
    () => (question ? decodeURIComponent(question) : ''),
    [question]
  );

  const { articles, isLoading, setIsLoading, handleSearch } = useArticleSearch();

  useEffect(() => {
    if (!decodedQuestion) return;

    setSearch(decodedQuestion);
    fetchedOnceRef.current = false;
    hydratedFromCacheRef.current = false;
    setCachedArticles(null);

    const cached = readCache();
    if (cached && cached.question === decodedQuestion) {
      hydratedFromCacheRef.current = true;
      setCachedArticles(cached.articles);
      setIsLoading(false);
      return;
    }

    if (fetchedOnceRef.current) return;
    fetchedOnceRef.current = true;

    (async () => {
      setIsLoading(true);
      await handleSearch(decodedQuestion);
      setIsLoading(false);
    })();
  }, [decodedQuestion]);

  useEffect(() => {
    if (!decodedQuestion) return;
    if (hydratedFromCacheRef.current) return;
    if (!articles || articles.length === 0) return;
    if (!fetchedOnceRef.current) return;

    writeCache({
      question: decodedQuestion,
      articles,
      total: articles.length,
      timestamp: Date.now(),
    });
  }, [articles, decodedQuestion]);

  const dataToRender = (cachedArticles ?? articles) || [];
  const total = dataToRender.length;

  return (
    <>
      <MainNavbar />
      <div className="ArticlesSearch" data-testid="ArticlesSearch">
        <div
          className="background-wrapper"
          style={{
            background: `url(${process.env.PUBLIC_URL}/assets/img/background-wrapper-2.png) no-repeat center center`,
            backgroundSize: 'cover',
          }}
        >
          <Container>
            <Row className="justify-content-center">
              <Col md={6} className="text-center">
                {!isLoading ? (
                  <h4 className="text-white mb-3">
                    <b>{total} resultado{total === 1 ? '' : 's'}</b> encontrado{total === 1 ? '' : 's'} para "{search}"
                  </h4>
                ) : (
                  <Skeleton height={25} baseColor="#2c3063" highlightColor="#222343" className="mb-3" />
                )}
                <FormAppHome search={search} disable={isLoading} />
              </Col>
            </Row>
          </Container>
        </div>

        <div>
          <Container>
            <Row className="justify-content-center">
              <Col md={6} className="text-start">
                {!isLoading ? (
                  <div className="list-wrapper">
                    {dataToRender?.map((item) => (
                      <div key={`${item.article_id}-${item.score}`} className="mb-4">
                        <Link to={`/articles/view/${item.article_id}`}>
                          <h5 className="m-0">{item.title}</h5>
                        </Link>
                        <p className="my-2" style={{ whiteSpace: 'pre-wrap' }}>
                          {item.chunk.length > 250
                            ? item.chunk.slice(0, 250) + '…'
                            : item.chunk}
                        </p>
                        <Link to={`/articles/view/${item.article_id}/${item.chunk_index}`}>
                          <button className="btn btn-secondary text-primary btn-view-full">Ver resposta completa <BvIcon name="chevronRight" variant="duo" weight={600} /></button>
                        </Link>
                        <Link to={`/articles/categories/view/${item.category_id}`}>
                          <div className="small">
                            {item.category}{' '}
                          </div>
                        </Link>
                      </div>
                    ))}
                    {!dataToRender?.length && (
                      <div className="text-muted py-5">Nenhum resultado.</div>
                    )}
                  </div>
                ) : (
                  <div className="loading-wrapper">
                    <div className="mb-4">
                      <Skeleton height={30} />
                      <Skeleton height={80} />
                      <Skeleton height={15} width={50} />
                    </div>
                    <div className="mb-4">
                      <Skeleton height={30} />
                      <Skeleton height={80} />
                      <Skeleton height={15} width={50} />
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </>
  );
};

export default ArticlesSearch;
